// The Real-time chart's candles on TradingView Lightweight Charts, like the IBKR
// chat chart (ibkr_fast/LwCharts.tsx). A refetch that only extends the bars on
// screen (Refresh) goes through series.update(), so the user's zoom / scroll
// stays; any other data (new range, resolution, compress, track) is set from
// scratch and fitted. The time scale is index based: nights, weekends and
// holidays leave no gaps. The live price and the price alert are price lines,
// independent of the last bar.

import {
  AutoscaleInfo,
  CandlestickData,
  CandlestickSeries,
  ColorType,
  createChart,
  CrosshairMode,
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  Logical,
  LineStyle,
  SeriesAttachedParameter,
  Time,
  UTCTimestamp,
} from "lightweight-charts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CONTAINER, usePriceLine, useResetCss } from "./ibkr_fast/LwCharts";
import { formatVolume } from "./utils/utils";

export interface RtCandle {
  date: string; // "YYYY-MM-DD HH:MM:SS[.ffffff]" exchange wall time, or "YYYY-MM-DD"
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
  period?: "pre" | "regular" | "post" | "closed"; // Yahoo only
}

export interface RtTrack {
  name: string;
  candles: RtCandle[];
  color: string; // rising candles
}

// one track: absolute prices + volume pane; two: both % normalized to their first open
export interface RtChartData {
  tracks: RtTrack[];
}

export const NON_INTRADAY_RESOLUTIONS = ["D", "W", "M", "1d", "1wk", "1mo"];
export const ALERT_COLOR = "#E67E22";

// sub-dollar tickers need more precision for a meaningful alert level
export function priceDecimals(price: number): number {
  const p = Math.abs(price);
  return p < 1 ? 4 : p < 10 ? 3 : 2;
}

const DOWN = "#7F7F7F";
const PRICE_LINE = "#7F7F7F";
const VOLUME = "rgba(100, 149, 237, 0.35)";
const GRID = "#EEEEEE";
const AXIS = "#CCCCCC";
const PRE_MARKET_SHADE = "rgba(255, 247, 153, 0.35)";
const POST_MARKET_SHADE = "rgba(255, 179, 102, 0.35)";
const HEIGHT = 450;

// ---- data ------------------------------------------------------------------------

// Lightweight Charts shows times as UTC: the exchange wall time passed as if it were UTC
function wallTime(date: string) {
  return (Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10), +date.slice(11, 13), +date.slice(14, 16), +date.slice(17, 19)) / 1000) as UTCTimestamp;
}

// compress: Yahoo bars by their session, Invcom intraday bars by 9:30-16:00
function regularHours(c: RtCandle, intraday: boolean) {
  if (c.period) return c.period === "regular";
  const hm = c.date.slice(11, 16);
  return !intraday || (hm >= "09:30" && hm < "16:00");
}

interface Point {
  t: UTCTimestamp;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number | null;
  period?: RtCandle["period"];
}

function toPoints(candles: RtCandle[], compress: boolean, intraday: boolean, normalize: boolean): Point[] {
  const rows = compress ? candles.filter((c) => regularHours(c, intraday)) : candles;
  const k = normalize && rows.length ? 100 / rows[0].open : 1;
  return rows.map((c) => ({ t: wallTime(c.date), o: c.open * k, h: c.high * k, l: c.low * k, c: c.close * k, v: c.volume, period: c.period }));
}

// index from which `next` only extends `prev` (the same bars up to prev's last one,
// which may have changed since), or -1 when it is different data
function extendsFrom(prev: Point[] | undefined, next: Point[]) {
  if (!prev?.length || next.length < prev.length) return -1;
  const i = prev.length - 1;
  return next[0].t === prev[0].t && next[i].t === prev[i].t ? i : -1;
}

const candle = (p: Point): CandlestickData<Time> => ({ time: p.t, open: p.o, high: p.h, low: p.l, close: p.c });
const volume = (p: Point) => ({ time: p.t, value: p.v ?? 0 });

// ---- pre / post market shading (Yahoo) ---------------------------------------------

interface Span { from: Time; to: Time; color: string }

function sessionSpans(points: Point[]): Span[] {
  const spans: Span[] = [];
  for (let i = 0; i < points.length; ) {
    const period = points[i].period;
    let j = i;
    while (j + 1 < points.length && points[j + 1].period === period) j++;
    if (period === "pre" || period === "post") {
      spans.push({ from: points[i].t, to: points[j].t, color: period === "pre" ? PRE_MARKET_SHADE : POST_MARKET_SHADE });
    }
    i = j + 1;
  }
  return spans;
}

// full-height background behind runs of pre / post market bars
class SessionShading implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private requestUpdate: (() => void) | null = null;
  private spans: Span[] = [];
  private readonly views = [{
    zOrder: () => "bottom" as const,
    renderer: () => ({ draw: (target: any) => this.draw(target) }),
  }];

  attached(param: SeriesAttachedParameter<Time>) {
    this.chart = param.chart;
    this.requestUpdate = param.requestUpdate;
  }

  detached() {
    this.chart = null;
    this.requestUpdate = null;
  }

  paneViews() {
    return this.views;
  }

  setSpans(spans: Span[]) {
    this.spans = spans;
    this.requestUpdate?.();
  }

  private draw(target: any) {
    const ts = this.chart?.timeScale();
    if (!ts || !this.spans.length) return;
    const half = ((ts.logicalToCoordinate(1 as Logical) ?? 0) - (ts.logicalToCoordinate(0 as Logical) ?? 0)) / 2;
    target.useBitmapCoordinateSpace(({ context, bitmapSize, horizontalPixelRatio }: any) => {
      for (const s of this.spans) {
        const x0 = ts.timeToCoordinate(s.from);
        const x1 = ts.timeToCoordinate(s.to);
        if (x0 === null || x1 === null) continue;
        const left = Math.round((x0 - half) * horizontalPixelRatio);
        const right = Math.round((x1 + half) * horizontalPixelRatio);
        context.fillStyle = s.color;
        context.fillRect(left, 0, right - left, bitmapSize.height);
      }
    });
  }
}

// ---- chart -----------------------------------------------------------------------

interface Series {
  chart: IChartApi;
  candles: ISeriesApi<"Candlestick">[];
  volume: ISeriesApi<"Histogram"> | null;
  shading: SessionShading;
}

export function RealtimeLwChart(props: {
  data: RtChartData;
  resolution: string;
  compress: boolean;
  lastPrice?: number | null;
  alertPrice?: number | null;
  onPriceClick?: (price: number) => void;
  refreshCount?: number;
  onBarHiddenRight?: (hidden: boolean) => void;
}) {
  const { data, resolution, compress, lastPrice, alertPrice } = props;
  const intraday = !NON_INTRADAY_RESOLUTIONS.includes(resolution);
  const compare = data.tracks.length > 1;
  const el = useRef<HTMLDivElement>(null);
  const [chart, setChart] = useState<IChartApi | null>(null);
  const series = useRef<Series | null>(null);
  const shown = useRef<Point[][] | null>(null);
  const byTime = useRef<Map<number, Point>[]>([]);
  const [legend, setLegend] = useState<(Point | null)[] | null>(null);
  useResetCss();

  // latest props for the chart's callbacks
  const onPriceClick = useRef(props.onPriceClick);
  onPriceClick.current = props.onPriceClick;
  const lines = useRef<number[]>([]);
  lines.current = compare ? [] : [lastPrice, alertPrice].filter((p): p is number => p != null);

  const points = useMemo(
    () => data.tracks.map((tr) => toPoints(tr.candles, compress, intraday, compare)),
    [data.tracks, compress, intraday, compare],
  );

  useEffect(() => {
    const c = createChart(el.current!, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#FFFFFF" },
        textColor: "#333333",
        fontSize: 11,
        panes: { separatorColor: GRID, separatorHoverColor: GRID },
      },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#999999", width: 1, style: LineStyle.Solid, labelBackgroundColor: "#999999" },
        horzLine: { color: "#999999", width: 1, style: LineStyle.Solid, labelBackgroundColor: "#999999" },
      },
      rightPriceScale: { borderColor: AXIS },
      leftPriceScale: { borderColor: AXIS, visible: compare },
      // minBarSpacing: the whole history fits when zoomed out; lockVisibleTimeRangeOnResize:
      // a resized panel (or page) keeps showing the same bars
      timeScale: { borderColor: AXIS, timeVisible: intraday, secondsVisible: false, rightOffset: 3, minBarSpacing: 0.01, lockVisibleTimeRangeOnResize: true },
    });
    // the live price and the alert stay in view, like Plotly's shapes did
    const autoscaleInfoProvider = (original: () => AutoscaleInfo | null) => {
      const info = original();
      if (!info?.priceRange || !lines.current.length) return info;
      const { minValue, maxValue } = info.priceRange;
      return { ...info, priceRange: { minValue: Math.min(minValue, ...lines.current), maxValue: Math.max(maxValue, ...lines.current) } };
    };
    const candles = (compare ? [0, 1] : [0]).map((i) => c.addSeries(CandlestickSeries, {
      priceLineVisible: false,
      lastValueVisible: false,
      priceScaleId: i === 0 ? "right" : "left",
      ...(i === 0 && !compare ? { autoscaleInfoProvider } : {}),
    }, 0));
    const vol = compare ? null : c.addSeries(HistogramSeries, { color: VOLUME, priceFormat: { type: "volume" }, priceLineVisible: false, lastValueVisible: false }, 1);
    if (vol) {
      c.panes()[0]?.setStretchFactor(0.78);
      c.panes()[1]?.setStretchFactor(0.22);
    }
    const shading = new SessionShading();
    candles[0].attachPrimitive(shading);

    c.subscribeCrosshairMove((p) => setLegend(p.time !== undefined ? byTime.current.map((m) => m.get(p.time as number) ?? null) : null));
    // a click (not a drag) in the price pane -> the price at that height
    c.subscribeClick((p) => {
      const cb = onPriceClick.current;
      if (!cb || !p.point || (p.paneIndex ?? 0) !== 0) return;
      const price = candles[0].coordinateToPrice(p.point.y);
      if (price != null && Number.isFinite(price)) cb(price);
    });

    // a chart in a hidden tab (Invcom / Yahoo) has no width, so its fit is redone
    // the first time it is shown
    const container = el.current!;
    let hidden = container.clientWidth === 0;
    let frame = 0;
    const resize = new ResizeObserver(() => {
      const nowHidden = container.clientWidth === 0;
      // next frame: after the chart's own autoSize has taken the new width
      if (hidden && !nowHidden) frame = requestAnimationFrame(() => c.timeScale().fitContent());
      hidden = nowHidden;
    });
    resize.observe(container);

    series.current = { chart: c, candles, volume: vol, shading };
    shown.current = null;
    setChart(c);
    return () => {
      resize.disconnect();
      cancelAnimationFrame(frame);
      series.current = null;
      c.remove();
    };
  }, [intraday, compare]);

  // the refreshCount the shown bars were loaded with: new bars under a newer one come from
  // a Refresh, which keeps the user's scroll / zoom instead of fitting
  const refreshCount = useRef(props.refreshCount);
  const shownRefresh = useRef(props.refreshCount);
  refreshCount.current = props.refreshCount;

  // bars: update() when the data only extends what is shown, else setData + fit
  useEffect(() => {
    const s = series.current;
    if (!chart || !s) return;
    const prev = shown.current;
    const isRefresh = !!prev && prev.length === points.length && refreshCount.current !== shownRefresh.current;
    shownRefresh.current = refreshCount.current;
    const froms = points.map((p, i) => extendsFrom(prev?.[i], p));
    if (prev && prev.length === points.length && froms.every((f) => f >= 0)) {
      points.forEach((p, i) => {
        for (const pt of p.slice(froms[i])) {
          s.candles[i].update(candle(pt));
          if (i === 0) s.volume?.update(volume(pt));
        }
      });
    } else {
      const ts = s.chart.timeScale();
      const range = isRefresh ? ts.getVisibleLogicalRange() : null;
      points.forEach((p, i) => s.candles[i].setData(p.map(candle)));
      s.volume?.setData(points[0].map(volume));
      if (range && prev?.[0].length && points[0].length) {
        // same bars in view: shift by however many bars the refresh added / dropped before them
        const at = (p: Point[], t: Time) => { const i = p.findIndex((pt) => pt.t >= t); return i < 0 ? p.length : i; };
        const shift = points[0][0].t <= prev[0][0].t ? at(points[0], prev[0][0].t) : -at(prev[0], points[0][0].t);
        ts.setVisibleLogicalRange({ from: range.from + shift, to: range.to + shift });
      } else {
        ts.fitContent();
      }
    }
    shown.current = points;
    byTime.current = points.map((p) => new Map(p.map((pt) => [pt.t as number, pt])));
    s.shading.setSpans(sessionSpans(points[0]));
    const last = points[0][points[0].length - 1];
    const precision = compare || !last ? 2 : priceDecimals(last.c);
    s.candles[0].applyOptions({ priceFormat: { type: "price", precision, minMove: 1 / 10 ** precision } });
  }, [chart, points, compare]);

  // rising colors; falling candles are gray, or hollow in compare mode
  useEffect(() => {
    series.current?.candles.forEach((sr, i) => {
      const color = data.tracks[i]?.color ?? DOWN;
      const down = compare ? color : DOWN;
      sr.applyOptions({
        upColor: color, borderUpColor: color, wickUpColor: color,
        downColor: compare ? "rgba(0,0,0,0)" : DOWN, borderDownColor: down, wickDownColor: down,
      });
    });
  }, [chart, data.tracks, compare]);

  const main = chart && !compare ? series.current?.candles[0] ?? null : null;
  usePriceLine(main, lastPrice, PRICE_LINE);
  usePriceLine(main, alertPrice, ALERT_COLOR);

  // "Bar to the right!": after a Refresh, warn while the newest bar lies past the right
  // edge of the view; cleared once the user scrolls / zooms it into view
  const [watchRightEdge, setWatchRightEdge] = useState(false);
  const [lastHidden, setLastHidden] = useState(false);
  useEffect(() => {
    if (props.refreshCount) setWatchRightEdge(true);
  }, [props.refreshCount]);
  useEffect(() => {
    const s = series.current;
    if (!chart || !s) return;
    const ts = s.chart.timeScale();
    const check = () => {
      const range = ts.getVisibleLogicalRange();
      const hidden = range ? (s.candles[0].barsInLogicalRange(range)?.barsAfter ?? 0) > 0 : false;
      setLastHidden(hidden);
      if (!hidden) setWatchRightEdge(false);
    };
    ts.subscribeVisibleLogicalRangeChange(check);
    return () => ts.unsubscribeVisibleLogicalRangeChange(check);
  }, [chart]);
  const onBarHiddenRight = props.onBarHiddenRight;
  const barHiddenRight = watchRightEdge && lastHidden;
  useEffect(() => onBarHiddenRight?.(barHiddenRight), [barHiddenRight, onBarHiddenRight]);

  // double click: back to the full view with auto-scaled prices
  function resetView() {
    const c = series.current?.chart;
    if (!c) return;
    c.timeScale().fitContent();
    c.priceScale("right").applyOptions({ autoScale: true });
    if (compare) c.priceScale("left").applyOptions({ autoScale: true });
  }

  const shownLegend = legend ?? points.map((p) => p[p.length - 1] ?? null);
  const fmt = (v: number) => v.toFixed(compare ? 2 : priceDecimals(v));
  return (
    <div style={{ position: "relative", height: HEIGHT, cursor: props.onPriceClick ? "crosshair" : undefined }} onDoubleClick={resetView}>
      <div style={{ position: "absolute", top: 4, left: compare ? 64 : 8, zIndex: 3, fontSize: 11, color: "#333333", background: "rgba(255,255,255,0.8)", pointerEvents: "none" }}>
        {shownLegend.map((p, i) => p && (
          <div key={i} style={{ whiteSpace: "nowrap" }}>
            {compare && <span style={{ color: data.tracks[i].color }}>{data.tracks[i].name} · </span>}
            {nyLabel(p.t, intraday)} · O {fmt(p.o)} H {fmt(p.h)} L {fmt(p.l)} C {fmt(p.c)}
            {!compare && ` · Vol ${p.v == null ? "—" : formatVolume(p.v)}`}
          </div>
        ))}
      </div>
      <div ref={el} className={CONTAINER} style={{ position: "absolute", inset: 0 }} />
    </div>
  );
}

// "2026-10-29 09:30" from a wall time passed as UTC
function nyLabel(t: number, intraday: boolean) {
  const s = new Date(t * 1000).toISOString().replace("T", " ");
  return s.slice(0, intraday ? 16 : 10);
}
